import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Clock, Loader2, Mail } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { BrandCard } from "./integrations/brand";
import { SCURO } from "./integrations/casella";
// Il client con nome restituisce gia' i dati della risposta (non la risposta axios).
import { api } from "@/api/client";
import { useTranslation } from "../hooks/useTranslation";

export default function PendingInvitations() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch pending invitations
  const { data: invitationsData, isLoading } = useQuery({
    queryKey: ["pendingInvitations"],
    queryFn: async () => {
      return await api.get("/invites/pending");
    },
  });

  // Accept invitation mutation
  const acceptMutation = useMutation({
    mutationFn: async (token) => {
      return await api.post(`/invites/${token}/accept`);
    },
    onSuccess: (data) => {
      toast({
        variant: "success",
        title: t("components.invitations.acceptSuccess"),
        description: t("components.invitations.acceptSuccessDesc", {
          name: data.tenant.name,
        }),
      });
      // Dopo il ricaricamento si e' gia' dentro l'organizzazione appena raggiunta.
      try {
        if (data?.tenant?.id) localStorage.setItem("activeTenantId", data.tenant.id);
      } catch {
        // Senza localStorage resta quella di prima: si cambia dal menu.
      }
      // Cancel any ongoing queries to prevent errors during reload
      queryClient.cancelQueries();
      // Reload to get updated tenant list
      setTimeout(() => {
        window.location.reload();
      }, 300);
    },
    onError: (error) => {
      toast({
        title: t("components.invitations.acceptFailed"),
        description:
          error.response?.data?.message || error.message || t("common.error"),
        variant: "destructive",
      });
    },
    retry: false,
  });

  const handleAccept = (token) => {
    if (acceptMutation.isPending) return;
    acceptMutation.mutate(token);
  };

  // Don't show anything if no invitations
  if (
    !isLoading &&
    (!invitationsData?.invitations || invitationsData.invitations.length === 0)
  ) {
    return null;
  }

  const giorno = (d) =>
    new Date(d).toLocaleDateString(i18n.language, { day: "numeric", month: "long", year: "numeric" });

  return (
    <section className="space-y-4">
      <div>
        <h2 className="flex items-center gap-2 text-base font-semibold text-[#020617]">
          <Mail className="h-4 w-4 text-[#A855F7]" aria-hidden="true" />
          {t("components.invitations.pendingTitle")}
        </h2>
        <p className="mt-0.5 max-w-2xl text-sm text-[#64748B]">
          {t("components.invitations.pendingDescription", { count: invitationsData?.total || 0 })}
        </p>
      </div>
      {isLoading ? (
        <Skeleton className="h-[84px] rounded-[14px]" />
      ) : (
        <div className="space-y-3">
          {invitationsData?.invitations?.map((invitation) => (
            <BrandCard key={invitation.id} className="flex flex-wrap items-center gap-4 p-5">
              <div className="min-w-[12rem] flex-1">
                <p className="truncate text-sm font-semibold text-[#020617]">{invitation.tenant.name}</p>
                <p className="mt-0.5 text-sm text-[#64748B]">
                  {t("components.invitations.invitedByAs", {
                    name: invitation.invited_by.name || invitation.invited_by.email,
                    role: t(`components.members.${invitation.role}`).toLowerCase(),
                  })}
                </p>
                <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-[#94A3B8]">
                  <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("components.invitations.expiresOn", { date: giorno(invitation.expires_at) })}
                </p>
              </div>
              <button
                type="button"
                className={`${SCURO} shrink-0`}
                onClick={() => handleAccept(invitation.invite_token)}
                disabled={acceptMutation.isPending}
              >
                {acceptMutation.isPending && acceptMutation.variables === invitation.invite_token ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <Check className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {t("components.invitations.accept")}
              </button>
            </BrandCard>
          ))}
        </div>
      )}
    </section>
  );
}
